/**
 * Inserimento manuale di un evento.
 *
 * Serve a partita conclusa, per completare o ricostruire gli eventi quando
 * il timer non li ha presi tutti: batteria esaurita, un cambio dimenticato.
 * Gli eventi sono l'unica fonte, e da qui passano anche le correzioni che
 * cambiano minuti e partecipazioni.
 *
 * Il minuto lo digita l'utente e viene validato con le stesse funzioni che
 * girano sul server, cosi il messaggio di errore arriva subito invece di
 * tornare indietro da una richiesta rifiutata.
 */

import { useState } from 'react';
import { OctagonXIcon } from 'lucide-react';
import {
  fineEffettivaPeriodo,
  formattaMinuto,
  intervalloMinutoManuale,
  offsetPeriodo,
  validaMinutoManuale,
} from '@calcio/shared';
import type { Evento, EventoIn, Formato, TipoEvento } from '@calcio/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { IconaEvento } from '../partite/eventi.js';
import { ETICHETTA_EVENTO } from '../partite/partita.js';
import type { GiocatoreInRosa } from './tipi.js';
import type { RispostaStato } from './useTimerPartita.js';

const confronto = new Intl.Collator('it', { sensitivity: 'base' });

const SENZA_GIOCATORE: TipoEvento[] = ['gol_subito', 'autogol_avversario'];

export function EventoManuale({
  partitaId,
  rosa,
  tipiAttivi,
  formato,
  eventi,
  aperto,
  onChiudi,
  onAggiunto,
}: {
  partitaId: string;
  rosa: Pick<GiocatoreInRosa, 'id' | 'numero' | 'cognome' | 'nome'>[];
  tipiAttivi: TipoEvento[];
  formato: Formato;
  eventi: readonly Evento[];
  aperto: boolean;
  onChiudi: () => void;
  onAggiunto?: (risposta: RispostaStato, partenza: number) => void;
}) {
  // Primo minuto giocato del periodo.
  const minutoIniziale = (p: number) => offsetPeriodo(p, formato) + 1;

  const [tipo, setTipo] = useState<TipoEvento>('gol');
  const [giocatoreId, setGiocatoreId] = useState<string>('');
  const [periodo, setPeriodo] = useState(1);
  const [minutoTesto, setMinutoTesto] = useState(() => String(minutoIniziale(1)));
  // Il giocatore mancante si segnala solo dopo il tentativo di salvare: un
  // form appena aperto non parte con un errore.
  const [mancaGiocatore, setMancaGiocatore] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [invio, setInvio] = useState(false);

  // Un errore del server riguarda i valori inviati: appena l'utente ne
  // cambia uno, il messaggio non descrive piu quello che ha davanti.
  const cambia =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      set(v);
      setMancaGiocatore(false);
      setErrore(null);
    };

  const scegliPeriodo = (p: number) => {
    setPeriodo(p);
    setMinutoTesto(String(minutoIniziale(p)));
    setErrore(null);
  };

  const fine = fineEffettivaPeriodo(eventi, periodo);
  const { minimo, massimo } = intervalloMinutoManuale(periodo, formato, fine);
  const minuto = minutoTesto.trim() === '' ? NaN : Number(minutoTesto);
  const controllo = Number.isInteger(minuto)
    ? validaMinutoManuale(minuto, periodo, formato, fine)
    : ({ valido: false, messaggio: 'Indica il minuto.' } as const);

  const serveGiocatore = !SENZA_GIOCATORE.includes(tipo);

  const salva = async () => {
    if (!controllo.valido) return;
    if (serveGiocatore && !giocatoreId) {
      setMancaGiocatore(true);
      return;
    }

    const evento: EventoIn = {
      id: crypto.randomUUID(),
      tipo: tipo as EventoIn['tipo'],
      giocatoreId: serveGiocatore ? giocatoreId : null,
      periodo,
      minuto,
      secondo: 0,
      orarioReale: new Date().toISOString(),
    };
    setInvio(true);
    setErrore(null);
    const partenza = Date.now();
    try {
      const risposta = await chiama<RispostaStato>(`/partite/${partitaId}/eventi`, {
        metodo: 'POST',
        corpo: { eventi: [evento] },
      });
      onChiudi();
      onAggiunto?.(risposta, partenza);
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Inserimento non riuscito.');
    } finally {
      setInvio(false);
    }
  };

  // Le voci della tendina: i tipi attivi della squadra, poi quelli senza
  // giocatore e le entrate e uscite, che ricostruiscono chi era in campo
  // anche per una partita chiusa senza timer.
  const voci: [TipoEvento, string][] = [
    ...tipiAttivi.map((t): [TipoEvento, string] => [t, ETICHETTA_EVENTO[t]]),
    ['gol_subito', ETICHETTA_EVENTO.gol_subito],
    ['autogol_avversario', ETICHETTA_EVENTO.autogol_avversario],
    ['ingresso', 'Entra in campo'],
    ['uscita', 'Esce dal campo'],
  ];
  const giocatori = [...rosa].sort(
    (a, b) => confronto.compare(a.cognome, b.cognome) || confronto.compare(a.nome, b.nome),
  );

  // Lo apre il comando «Aggiungi evento» dell'elenco eventi: è lavoro da
  // scrivania, con le misure dei modali delle altre viste.
  return (
    <Dialog open={aperto} onOpenChange={(o) => !o && onChiudi()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aggiungi evento</DialogTitle>
          <DialogDescription>
            Il minuto si indica a mano; salvando si ricalcolano minuti giocati e partecipazioni.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void salva();
          }}
        >
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="manuale-tipo">Tipo</FieldLabel>
              <Select value={tipo} onValueChange={(v) => cambia(setTipo)(v as TipoEvento)}>
                <SelectTrigger id="manuale-tipo" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {voci.map(([t, etichetta]) => (
                      <SelectItem key={t} value={t}>
                        <IconaEvento tipo={t} />
                        {etichetta}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {tipo === 'ingresso' && (
                <FieldDescription>I titolari entrano al minuto 0 del 1° periodo.</FieldDescription>
              )}
            </Field>

            {serveGiocatore && (
              <Field data-invalid={mancaGiocatore}>
                <FieldLabel htmlFor="manuale-giocatore">Giocatore</FieldLabel>
                <Select value={giocatoreId} onValueChange={cambia(setGiocatoreId)}>
                  <SelectTrigger
                    id="manuale-giocatore"
                    aria-invalid={mancaGiocatore}
                    className="w-full"
                  >
                    <SelectValue placeholder="Scegli il giocatore" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {giocatori.map((g) => (
                        <SelectItem key={g.id} value={g.id}>
                          <span className="w-6 text-right text-muted-foreground tabular-nums">
                            {g.numero ?? '—'}
                          </span>
                          {g.cognome} {g.nome}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                {mancaGiocatore && <FieldError>Scegli il giocatore.</FieldError>}
              </Field>
            )}

            <div className="grid grid-cols-2 gap-4">
              <Field>
                <FieldLabel>Periodo</FieldLabel>
                <ToggleGroup
                  type="single"
                  variant="outline"
                  className="w-full"
                  value={String(periodo)}
                  onValueChange={(v) => v && scegliPeriodo(Number(v))}
                >
                  {Array.from({ length: formato.nPeriodi }, (_, i) => i + 1).map((p) => (
                    <ToggleGroupItem key={p} value={String(p)} className="flex-1">
                      {p}°
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </Field>

              <Field data-invalid={!controllo.valido}>
                <FieldLabel htmlFor="manuale-minuto">Minuto</FieldLabel>
                <Input
                  id="manuale-minuto"
                  type="number"
                  inputMode="numeric"
                  className="tabular-nums"
                  value={minutoTesto}
                  min={minimo}
                  max={massimo}
                  aria-invalid={!controllo.valido}
                  onChange={(e) => cambia(setMinutoTesto)(e.target.value)}
                />
              </Field>
            </div>
            {/* Si mostra la notazione risultante, cosi chi digita 47 vede subito
                che sta registrando al 45+2 e non si chiede se ha sbagliato. Accanto
                c'e l'intervallo ammesso nel periodo scelto, nella stessa notazione. */}
            <Field data-invalid={!controllo.valido}>
              <FieldDescription className="tabular-nums">
                <span className="font-semibold text-foreground">
                  {Number.isInteger(minuto) ? formattaMinuto(minuto, periodo, formato) : '—'}
                </span>
                {' · '}da {formattaMinuto(minimo, periodo, formato)} a{' '}
                {formattaMinuto(massimo, periodo, formato)}
              </FieldDescription>
              {!controllo.valido && <FieldError>{controllo.messaggio}</FieldError>}
            </Field>
          </FieldGroup>

          {errore && (
            <Alert variant="destructive">
              <OctagonXIcon />
              <AlertTitle>Evento non aggiunto</AlertTitle>
              <AlertDescription>{errore}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onChiudi}>
              Annulla
            </Button>
            <PulsanteInvio type="submit" inCorso={invio} disabled={!controllo.valido}>
              Aggiungi
            </PulsanteInvio>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
